import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api, { fileUrl } from "@/lib/api";
import { CalendarOff } from "lucide-react";
import { toast } from "sonner";
import {
  POLL_MS, COL_W,
  formatRangeDay, dayOffset, addDaysToIso, dayIndexToIso,
} from "@/components/calendar/calendarHelpers";
import { toLocalIso } from "@/lib/localDate";
import ReturnWizard from "@/components/ops/ReturnWizard";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/** Iter 100 — color legend (spec §1). */
const LEGEND = [
  ["bg-emerald-500", "Available"],
  ["bg-blue-500", "Reserved"],
  ["bg-yellow-400", "Hold / Payment pending"],
  ["bg-orange-500", "Active rental"],
  ["bg-rose-500", "Blocked"],
  ["bg-red-800", "Maintenance / Accident"],
  ["bg-neutral-400", "Completed"],
];

/** Iter 100 — live per-vehicle status for the row label (spec §3). */
function laneStatusFor(car, carBookings) {
  const today = toLocalIso(new Date());
  const vs = (car.vehicle_status || "").toLowerCase();
  if (["maintenance", "accident", "out_of_service"].includes(vs)) {
    return { dot: "bg-red-800", label: vs === "accident" ? "Accident" : vs === "out_of_service" ? "Out of service" : "Maintenance", cls: "text-red-800" };
  }
  if (vs === "blocked") return { dot: "bg-rose-500", label: "Blocked", cls: "text-rose-600" };
  for (const r of car.unavailable_ranges || []) {
    if (String(r.start || "").slice(0, 10) <= today && String(r.end || "").slice(0, 10) >= today) {
      return { dot: "bg-rose-500", label: `Blocked${r.reason ? ` · ${r.reason}` : ""}`, cls: "text-rose-600" };
    }
  }
  for (const b of carBookings) {
    const s = String(b.start || "").slice(0, 10);
    const e = String(b.end || "").slice(0, 10);
    if (s <= today && today <= e) {
      if (b.status === "confirmed" && !b.returned_at) {
        return { dot: "bg-orange-500", label: `Rented until ${formatRangeDay(b.end)}`, cls: "text-orange-600" };
      }
      if (b.status === "pending_payment") {
        return { dot: "bg-yellow-400", label: "Temporary hold", cls: "text-yellow-700" };
      }
      if (b.early_return && e === today) {
        return { dot: "bg-emerald-500", label: "Available (returned early)", cls: "text-emerald-700", early: true };
      }
    }
  }
  return { dot: "bg-emerald-500", label: "Available", cls: "text-emerald-700" };
}
import CalendarToolbar from "@/components/calendar/CalendarToolbar";
import CalendarBookingBar from "@/components/calendar/CalendarBookingBar";
import CalendarUnavailStrip from "@/components/calendar/CalendarUnavailStrip";
import CalendarBlockDialog from "@/components/calendar/CalendarBlockDialog";
import CalendarBlockEditDialog from "@/components/calendar/CalendarBlockEditDialog";
import CalendarReservationSearch from "@/components/calendar/CalendarReservationSearch";

export default function BookingsCalendar({ scope = "vendor", initialDays = 30 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [windowStart, setWindowStart] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  // Iter 98 — phones default to a 3-day view; desktop keeps the wide window.
  const [windowDays, setWindowDays] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 768 ? 3 : initialDays,
  );
  const [vendorFilter, setVendorFilter] = useState("");
  const [carFilter, setCarFilter] = useState("");
  // Iter 74j — quick filter by gearbox. "" = all, "A" = automatic, "M" = manual.
  const [gearboxFilter, setGearboxFilter] = useState("");
  // Iter 100 — early check-in + extension straight from the calendar.
  const [earlyTarget, setEarlyTarget] = useState(null);
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendDate, setExtendDate] = useState("");
  const [extendBusy, setExtendBusy] = useState(false);

  const openEarlyReturn = (booking, car) => {
    setEarlyTarget({
      id: booking.id,
      deposit_amount: 0,
      car: { make: car?.make, model: car?.model, license_plate: car?.plate },
    });
  };
  const openExtend = (booking, car) => {
    setExtendTarget({ ...booking, carLabel: `${car?.make || ""} ${car?.model || ""}` });
    setExtendDate(addDaysToIso(String(booking.end || "").slice(0, 10), 1));
  };
  const submitExtend = async () => {
    setExtendBusy(true);
    try {
      await api.post(`/bookings/${extendTarget.id}/extend`, { new_end_date: extendDate });
      toast.success(`Rental extended to ${formatRangeDay(extendDate)}`);
      setExtendTarget(null);
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not extend rental");
    } finally {
      setExtendBusy(false);
    }
  };

  // Iter 78 — reservation-number search: highlighted booking bar id.
  const [highlightId, setHighlightId] = useState(null);

  // Drag-to-add unavailable range state (vendor only).
  const [dragging, setDragging] = useState(null);
  const [pending, setPending] = useState(null);
  const [pendingReason, setPendingReason] = useState("");
  const [savingPending, setSavingPending] = useState(false);
  // Drag-to-resize existing strips.
  const [resizing, setResizing] = useState(null);
  // Iter 84 — click-to-edit an existing blocked range.
  const [editBlock, setEditBlock] = useState(null); // {carId, range}
  const [editBusy, setEditBusy] = useState(null); // "save" | "delete" | null
  const laneRefs = useRef({});

  const endpoint = scope === "admin" ? "/admin/calendar" : "/vendor/calendar";

  const load = useCallback(async (background = false) => {
    if (background) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setData(data);
    } catch (e) {
      console.error("calendar load failed:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [endpoint]);

  useEffect(() => {
    load(false);
    const t = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const days = useMemo(() => {
    return Array.from({ length: windowDays }, (_, i) => {
      const d = new Date(windowStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [windowStart, windowDays]);

  // Group consecutive days by month-year so the header shows e.g. "February 2026 | March 2026"
  const monthSpans = useMemo(() => {
    const spans = [];
    days.forEach((d) => {
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      const label = d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
      const last = spans[spans.length - 1];
      if (last && last.key === key) last.count += 1;
      else spans.push({ key, label, count: 1 });
    });
    return spans;
  }, [days]);

  const cars = useMemo(() => {
    if (!data) return [];
    let list = data.cars;
    if (vendorFilter) list = list.filter((c) => c.vendor_id === vendorFilter);
    if (carFilter) list = list.filter((c) => c.id === carFilter);
    if (gearboxFilter) {
      const target = gearboxFilter.toLowerCase();
      list = list.filter((c) => String(c.transmission || "").toLowerCase().startsWith(target));
    }
    return list;
  }, [data, vendorFilter, carFilter, gearboxFilter]);

  const bookingsByCar = useMemo(() => {
    if (!data) return {};
    const map = {};
    for (const b of data.bookings) {
      (map[b.car_id] = map[b.car_id] || []).push(b);
    }
    return map;
  }, [data]);

  // Iter 74m — utilisation % per car within the visible window. Counts each
  // day (out of `windowDays`) that is covered by either a booking or an
  // unavailable range. Bookings and unavail ranges can overlap — we OR the
  // busy days into a Set so a single day never double-counts.
  const utilByCar = useMemo(() => {
    if (!data) return {};
    const startMs = windowStart.getTime();
    const dayMs = 86400000;
    const rangeToDays = (startIso, endIso) => {
      if (!startIso || !endIso) return [];
      const s = new Date(startIso); s.setHours(0, 0, 0, 0);
      const e = new Date(endIso); e.setHours(0, 0, 0, 0);
      const days = [];
      for (let d = s.getTime(); d <= e.getTime(); d += dayMs) {
        const idx = Math.round((d - startMs) / dayMs);
        if (idx >= 0 && idx < windowDays) days.push(idx);
      }
      return days;
    };
    const util = {};
    for (const car of data.cars) {
      const busy = new Set();
      const cb = bookingsByCar[car.id] || [];
      for (const b of cb) {
        if (b.status !== "cancelled") {
          // The calendar payload flattens booking dates to `start`/`end`
          // (see _build_calendar_payload in core.py).
          rangeToDays(b.start, b.end).forEach((i) => busy.add(i));
        }
      }
      for (const u of car.unavailable_ranges || []) {
        rangeToDays(u.start, u.end).forEach((i) => busy.add(i));
      }
      util[car.id] = Math.round((busy.size / windowDays) * 100);
    }
    return util;
  }, [data, bookingsByCar, windowStart, windowDays]);

  // Vendor list (admin only)
  const vendors = useMemo(() => {
    if (!data || scope !== "admin") return [];
    const seen = {};
    for (const c of data.cars) {
      if (c.vendor_id && !seen[c.vendor_id]) {
        seen[c.vendor_id] = { id: c.vendor_id, name: c.vendor_name };
      }
    }
    return Object.values(seen).sort((a, b) => a.name.localeCompare(b.name));
  }, [data, scope]);

  const shiftWindow = (deltaDays) => {
    const next = new Date(windowStart);
    next.setDate(next.getDate() + deltaDays);
    setWindowStart(next);
  };

  const jumpToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setWindowStart(d);
  };

  // Iter 78 — picked a reservation from the search dropdown: reposition the
  // window so the booking is visible, drop any filters that could hide the
  // car row, and pulse-highlight the bar.
  const handleSearchSelect = (b) => {
    const d = new Date(b.start);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 2);
    setWindowStart(d);
    setVendorFilter("");
    setCarFilter("");
    setGearboxFilter("");
    setHighlightId(b.id);
  };

  // Convert a clientX over a lane element → integer day index (0..windowDays-1)
  const clientXToDayIndex = (clientX, laneEl) => {
    const rect = laneEl.getBoundingClientRect();
    const idx = Math.floor((clientX - rect.left) / COL_W);
    return Math.max(0, Math.min(windowDays - 1, idx));
  };

  // Was the mousedown target an interactive child? If so, skip the create-drag.
  const isInteractiveTarget = (target) =>
    target?.closest?.("[data-testid^='cal-booking-']") ||
    target?.closest?.("[data-testid^='cal-unavail-']") ||
    target?.closest?.("[data-testid^='cal-resize-']");

  const onLanePointerDown = (e, carId) => {
    if (scope !== "vendor") return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (isInteractiveTarget(e.target)) return;
    const lane = laneRefs.current[carId];
    if (!lane) return;
    e.preventDefault();
    const startIdx = clientXToDayIndex(e.clientX, lane);
    setDragging({ carId, startDay: startIdx, endDay: startIdx });
    const onMove = (ev) => {
      const idx = clientXToDayIndex(ev.clientX, lane);
      setDragging((prev) => (prev ? { ...prev, endDay: idx } : prev));
    };
    const onUp = (ev) => {
      const idx = clientXToDayIndex(ev.clientX, lane);
      const lo = Math.min(startIdx, idx);
      const hi = Math.max(startIdx, idx);
      setPending({ carId, startDay: lo, endDay: hi + 1 });
      setPendingReason("");
      setDragging(null);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  // Drag-to-resize an existing unavailable strip. `edge` is "left" (moves start) or "right" (moves end).
  const onResizePointerDown = (e, carId, range, edge) => {
    if (scope !== "vendor") return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* older browsers */ }
    const startClientX = e.clientX;
    setResizing({ carId, rangeId: range.id || range.start, edge, originalStart: range.start, originalEnd: range.end, deltaDays: 0 });

    const onMove = (ev) => {
      const delta = Math.round((ev.clientX - startClientX) / COL_W);
      setResizing((prev) => (prev ? { ...prev, deltaDays: delta } : prev));
    };
    const onUp = async (ev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      const delta = Math.round((ev.clientX - startClientX) / COL_W);
      if (!delta) { setResizing(null); return; }

      const car = (data?.cars || []).find((c) => c.id === carId);
      if (!car) { setResizing(null); return; }

      const next = (car.unavailable_ranges || []).map((r) => {
        if ((r.id || r.start) !== (range.id || range.start)) return r;
        const newStart = edge === "left" ? addDaysToIso(r.start, delta) : r.start;
        const newEnd = edge === "right" ? addDaysToIso(r.end, delta) : r.end;
        return { ...r, start: newStart, end: newEnd };
      }).filter((r) => r.end > r.start);

      const original = (car.unavailable_ranges || []).find((r) => (r.id || r.start) === (range.id || range.start));
      const newOne = next.find((r) => r.start === (edge === "left" ? addDaysToIso(original.start, delta) : original.start));
      if (!newOne) {
        toast.error("Range collapsed to zero days — drag the other edge instead.");
        setResizing(null);
        return;
      }

      try {
        await api.patch(`/cars/${carId}`, { unavailable_ranges: next });
        toast.success("Range updated");
        await load(true);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not save");
      } finally {
        setResizing(null);
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const cancelPending = () => {
    setPending(null);
    setPendingReason("");
  };

  // Iter 84 — edit / remove an existing blocked range via dialog.
  const openBlockEditor = (carId, range) => {
    if (scope !== "vendor" || resizing) return;
    setEditBlock({ carId, range });
  };

  const saveBlockEdit = async ({ start, end, reason }) => {
    if (!editBlock) return;
    const car = (data?.cars || []).find((c) => c.id === editBlock.carId);
    if (!car) return;
    const rid = editBlock.range.id || editBlock.range.start;
    const next = (car.unavailable_ranges || []).map((r) =>
      (r.id || r.start) === rid ? { ...r, start, end, reason } : r,
    );
    setEditBusy("save");
    try {
      await api.patch(`/cars/${editBlock.carId}`, { unavailable_ranges: next });
      toast.success("Blocked dates updated");
      setEditBlock(null);
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setEditBusy(null);
    }
  };

  const deleteBlockEdit = async () => {
    if (!editBlock) return;
    const car = (data?.cars || []).find((c) => c.id === editBlock.carId);
    if (!car) return;
    const rid = editBlock.range.id || editBlock.range.start;
    const next = (car.unavailable_ranges || []).filter((r) => (r.id || r.start) !== rid);
    setEditBusy("delete");
    try {
      await api.patch(`/cars/${editBlock.carId}`, { unavailable_ranges: next });
      toast.success("Block removed — dates are bookable again");
      setEditBlock(null);
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    } finally {
      setEditBusy(null);
    }
  };

  const savePending = async ({ repeatWeeks } = {}) => {
    if (!pending) return;
    const car = (data?.cars || []).find((c) => c.id === pending.carId);
    if (!car) return;
    setSavingPending(true);
    try {
      const baseStart = dayIndexToIso(pending.startDay, windowStart);
      const baseEnd = dayIndexToIso(pending.endDay, windowStart);
      const reason = pendingReason.trim();
      // Iter 94 — recurring weekly block: one range per week for N weeks.
      const occurrences = repeatWeeks && repeatWeeks > 0 ? repeatWeeks : 1;
      const newRanges = Array.from({ length: occurrences }, (_, i) => ({
        start: addDaysToIso(baseStart, i * 7),
        end: addDaysToIso(baseEnd, i * 7),
        reason: occurrences > 1 && reason ? `${reason} (weekly)` : reason,
      }));
      const next = [...(car.unavailable_ranges || []), ...newRanges];
      await api.patch(`/cars/${pending.carId}`, { unavailable_ranges: next });
      toast.success(occurrences > 1 ? `Blocked ${occurrences} weekly ranges` : "Unavailable range added");
      setPending(null);
      setPendingReason("");
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSavingPending(false);
    }
  };

  if (loading) {
    return <div className="text-neutral-500 py-12 text-center" data-testid="bookings-calendar-loading">Loading calendar…</div>;
  }
  if (!data) {
    return <div className="text-neutral-500 py-12 text-center">Could not load calendar.</div>;
  }

  const pendingCar = pending ? (data?.cars || []).find((c) => c.id === pending.carId) : null;
  const pendingStartIso = pending ? dayIndexToIso(pending.startDay, windowStart) : null;
  const pendingEndIso = pending ? dayIndexToIso(pending.endDay - 1, windowStart) : null; // inclusive for display
  const pendingDays = pending ? pending.endDay - pending.startDay : 0;

  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid={`bookings-calendar-${scope}`}>
      <CalendarToolbar
        carsCount={cars.length}
        bookingsCount={data.bookings.length}
        windowDays={windowDays}
        setWindowDays={setWindowDays}
        onPrev={() => shiftWindow(-windowDays)}
        onToday={jumpToday}
        onNext={() => shiftWindow(windowDays)}
        onRefresh={() => load(true)}
        refreshing={refreshing}
        gearboxFilter={gearboxFilter}
        setGearboxFilter={setGearboxFilter}
      />

      {/* Iter 78 — reservation-number fast search. */}
      <div className="px-5 py-2 border-b border-border bg-neutral-50/40">
        <CalendarReservationSearch
          bookings={data.bookings}
          cars={data.cars}
          onSelect={handleSearchSelect}
          onClear={() => setHighlightId(null)}
        />
      </div>

      {/* Admin-only filter bar */}
      {scope === "admin" && vendors.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap px-5 py-2 border-b border-border text-xs">
          <span className="text-neutral-400 uppercase tracking-wider font-semibold">Vendor:</span>
          <button
            onClick={() => setVendorFilter("")}
            className={`px-2.5 py-0.5 rounded-full border transition ${vendorFilter === "" ? "bg-neutral-900 text-white border-neutral-900" : "border-border hover:border-neutral-400"}`}
            data-testid="cal-vendor-all"
          >
            All ({vendors.length})
          </button>
          {vendors.map((v) => (
            <button
              key={v.id}
              onClick={() => setVendorFilter(v.id === vendorFilter ? "" : v.id)}
              className={`px-2.5 py-0.5 rounded-full border transition ${vendorFilter === v.id ? "bg-neutral-900 text-white border-neutral-900" : "border-border hover:border-neutral-400"}`}
              data-testid={`cal-vendor-${v.id}`}
            >
              {v.name}
            </button>
          ))}
        </div>
      )}

      {/* Timeline grid */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 pb-2" data-testid="cal-legend">
        {LEGEND.map(([dot, label]) => (
          <span key={label} className="inline-flex items-center gap-1.5 text-[11px] text-neutral-600 font-medium">
            <span className={`h-2.5 w-2.5 rounded-sm ${dot}`} /> {label}
          </span>
        ))}
      </div>
      <div className="overflow-x-auto">
        <div style={{ minWidth: 200 + windowDays * COL_W }}>
          {/* Month band */}
          <div className="flex sticky top-0 bg-neutral-50 z-20 border-b border-border" data-testid="cal-month-band">
            <div className="w-[200px] shrink-0 px-4 py-1.5 text-[10px] uppercase tracking-wider text-neutral-500 font-semibold border-r border-border">Month</div>
            <div className="flex">
              {monthSpans.map((m, idx) => (
                <div
                  key={m.key}
                  style={{ width: m.count * COL_W }}
                  className={`text-center py-1.5 text-xs font-semibold text-neutral-700 border-r border-border last:border-r-0 ${idx % 2 === 1 ? "bg-blue-50/60 text-blue-800" : ""}`}
                  data-testid={`cal-month-${m.key}`}
                >
                  {m.label}
                </div>
              ))}
            </div>
          </div>

          {/* Date header */}
          <div className="flex sticky top-7 bg-white z-10 border-b border-border">
            <div className="w-[200px] shrink-0 px-4 py-2 text-xs uppercase tracking-wider text-neutral-500 font-semibold border-r border-border">Car</div>
            <div className="flex">
              {days.map((d) => {
                const isToday = d.toDateString() === new Date().toDateString();
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                // Iter 65 P2: derive the iso key from LOCAL parts so it matches what
                // the user sees in `d.getDate()` below. Using `toISOString()` here
                // produced UTC-shifted keys that diverged from the displayed day for
                // any user east of UTC.
                const iso = toLocalIso(d);
                const headerToneClass = (() => {
                  if (isToday) return "bg-blue-50 text-blue-700 font-bold";
                  if (isWeekend) return "bg-neutral-50 text-neutral-500";
                  return "text-neutral-600";
                })();
                return (
                  <div
                    key={iso}
                    style={{ width: COL_W }}
                    className={`text-center py-1.5 text-[10px] border-r border-border last:border-r-0 ${headerToneClass}`}
                    data-testid={`cal-day-header-${iso}`}
                  >
                    <div>{d.toLocaleDateString("en-GB", { weekday: "short" })}</div>
                    <div className="font-semibold text-xs">{d.getDate()}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Car rows */}
          {cars.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-neutral-500" data-testid="cal-empty">No cars to display.</div>
          ) : (
            cars.map((car) => {
              const carBookings = bookingsByCar[car.id] || [];
              const carUnavail = car.unavailable_ranges || [];
              return (
                <div key={car.id} className="flex border-b border-border last:border-b-0 hover:bg-neutral-50/50 transition" data-testid={`cal-car-row-${car.id}`}>
                  <div className="w-[200px] shrink-0 px-4 py-3 border-r border-border">
                    <button
                      onClick={() => setCarFilter(car.id === carFilter ? "" : car.id)}
                      className="text-left w-full"
                      data-testid={`cal-car-name-${car.id}`}
                    >
                      <div className="font-medium text-sm truncate flex items-center gap-1.5">
                        {/* Iter 98 — vehicle thumbnail on the calendar lane. */}
                        {car.images?.[0] && (
                          <img
                            src={fileUrl(car.images[0])}
                            alt=""
                            className="h-7 w-10 rounded-md object-cover shrink-0 border border-border"
                            loading="lazy"
                            data-testid={`cal-car-thumb-${car.id}`}
                          />
                        )}
                        <span className="truncate">{car.make} {car.model}</span>
                        {(() => {
                          // Iter 74i — one-letter gearbox chip.
                          // A = automatic (blue), M = manual (amber). Hides
                          // entirely when the transmission field is missing.
                          const t = String(car.transmission || "").toLowerCase();
                          if (t.startsWith("a")) {
                            return (
                              <span
                                className="inline-flex items-center justify-center h-4 w-4 rounded bg-blue-100 text-blue-700 text-[10px] font-bold shrink-0"
                                title="Automatic gearbox"
                                data-testid={`cal-gearbox-A-${car.id}`}
                              >A</span>
                            );
                          }
                          if (t.startsWith("m")) {
                            return (
                              <span
                                className="inline-flex items-center justify-center h-4 w-4 rounded bg-amber-100 text-amber-800 text-[10px] font-bold shrink-0"
                                title="Manual gearbox"
                                data-testid={`cal-gearbox-M-${car.id}`}
                              >M</span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      <div className="text-xs text-neutral-500 truncate">{car.location}</div>
                      {/* Iter 100 — live status line (spec §3). */}
                      {(() => {
                        const st = laneStatusFor(car, carBookings);
                        return (
                          <div className={`flex items-center gap-1.5 mt-0.5 text-[11px] font-semibold ${st.cls}`} data-testid={`cal-lane-status-${car.id}`}>
                            <span className={`h-2 w-2 rounded-full ${st.dot}`} />
                            <span className="truncate">{st.label}</span>
                          </div>
                        );
                      })()}
                      {scope === "admin" && (
                        <div className="text-[10px] text-neutral-400 truncate">{car.vendor_name}</div>
                      )}
                      {!car.is_active && (
                        <span className="inline-block mt-1 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-neutral-200 text-neutral-600">Paused</span>
                      )}
                      {/* Iter 74m — % booked bar over the visible window.
                          Green (≥60%), amber (30-59%), rose (<30%). Lets
                          admins spot underperforming cars at a glance. */}
                      {(() => {
                        const pct = utilByCar[car.id] || 0;
                        const tone = pct >= 60
                          ? { bar: "bg-emerald-500", txt: "text-emerald-700" }
                          : pct >= 30
                            ? { bar: "bg-amber-500", txt: "text-amber-700" }
                            : { bar: "bg-rose-500", txt: "text-rose-700" };
                        return (
                          <div
                            className="mt-1.5 flex items-center gap-1.5"
                            title={`${pct}% booked over the next ${windowDays} days`}
                            data-testid={`cal-util-${car.id}`}
                          >
                            <div className="flex-1 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                              <div
                                className={`h-full ${tone.bar} rounded-full transition-all`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className={`text-[10px] font-semibold ${tone.txt} tabular-nums shrink-0`}>
                              {pct}%
                            </span>
                          </div>
                        );
                      })()}
                    </button>
                  </div>
                  {/* Lane */}
                  <div
                    ref={(el) => { if (el) laneRefs.current[car.id] = el; else delete laneRefs.current[car.id]; }}
                    onPointerDown={(e) => onLanePointerDown(e, car.id)}
                    className={`relative ${scope === "vendor" ? "cursor-crosshair select-none touch-none" : ""}`}
                    style={{ width: windowDays * COL_W, height: 60 }}
                    data-testid={`cal-lane-${car.id}`}
                  >
                    {/* Day grid lines */}
                    <div className="absolute inset-0 flex">
                      {days.map((d) => {
                        const isToday = d.toDateString() === new Date().toDateString();
                        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                        const laneToneClass = isToday
                          ? "bg-blue-50/40"
                          : isWeekend
                          ? "bg-neutral-50/60"
                          : "";
                        return (
                          <div
                            key={toLocalIso(d)}
                            style={{ width: COL_W }}
                            className={`border-r border-border/60 last:border-r-0 ${laneToneClass}`}
                          />
                        );
                      })}
                    </div>
                    {/* Drag preview (this car only) */}
                    {dragging && dragging.carId === car.id && (() => {
                      const lo = Math.min(dragging.startDay, dragging.endDay);
                      const hi = Math.max(dragging.startDay, dragging.endDay);
                      const left = lo * COL_W;
                      const width = (hi - lo + 1) * COL_W;
                      return (
                        <div
                          className="absolute top-1 bottom-1 rounded-md border-2 border-rose-400 bg-rose-100/70 pointer-events-none flex items-center justify-center text-[10px] text-rose-800 font-semibold"
                          style={{ left, width, backgroundImage: "repeating-linear-gradient(45deg, transparent 0 4px, rgba(244,63,94,0.18) 4px 8px)" }}
                          data-testid={`cal-drag-preview-${car.id}`}
                        >
                          {hi - lo + 1} day{hi - lo === 0 ? "" : "s"}
                        </div>
                      );
                    })()}
                    {/* Unavailable strips */}
                    {carUnavail.map((r) => (
                      <CalendarUnavailStrip
                        key={r.id || r.start}
                        car={car}
                        range={r}
                        scope={scope}
                        resizing={resizing}
                        windowStart={windowStart}
                        windowDays={windowDays}
                        onResizePointerDown={onResizePointerDown}
                        onEdit={openBlockEditor}
                      />
                    ))}
                    {/* Booking bars */}
                    {carBookings.map((b) => {
                      const s = dayOffset(b.start, windowStart);
                      const e = dayOffset(b.end, windowStart);
                      if (e === null || s === null || e <= 0 || s >= windowDays) return null;
                      const left = Math.max(0, s) * COL_W;
                      const right = Math.min(windowDays, e) * COL_W;
                      return (
                        <CalendarBookingBar
                          key={b.id}
                          booking={b}
                          left={left}
                          width={right - left}
                          car={car}
                          scope={scope}
                          highlighted={b.id === highlightId}
                          onEarlyReturn={openEarlyReturn}
                          onExtend={openExtend}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Footer: tips + last refresh (legend lives above the grid — Iter 100). */}
      <div className="flex items-center justify-between flex-wrap gap-2 px-5 py-2 border-t border-border bg-neutral-50/40 text-[11px] text-neutral-500">
        <div className="flex items-center gap-3 flex-wrap">
          {scope === "vendor" && (
            <span className="inline-flex items-center gap-1.5 text-neutral-400" data-testid="cal-drag-hint">
              <CalendarOff className="h-3 w-3" /> Tip: tap & drag a row to block dates · click a block to edit or remove it · drag its edges to resize
            </span>
          )}
        </div>
        <div data-testid="cal-last-updated">Auto-refreshes every {Math.round(POLL_MS / 1000)}s · last update {new Date(data.fetched_at).toLocaleTimeString()}</div>
      </div>

      <CalendarBlockDialog
        open={!!pending}
        car={pendingCar}
        startIso={pendingStartIso}
        endIso={pendingEndIso}
        days={pendingDays}
        reason={pendingReason}
        setReason={setPendingReason}
        onCancel={cancelPending}
        onSave={savePending}
        saving={savingPending}
      />
      {/* Iter 100 — early check-in wizard + extension dialog from the calendar. */}
      <ReturnWizard
        booking={earlyTarget}
        onClose={() => setEarlyTarget(null)}
        onDone={() => { setEarlyTarget(null); load(true); }}
      />
      <Dialog open={!!extendTarget} onOpenChange={(o) => !o && setExtendTarget(null)}>
        <DialogContent data-testid="extend-dialog">
          <DialogHeader>
            <DialogTitle>Extend rental — {extendTarget?.carLabel}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-neutral-600">
            Current return: <span className="font-semibold">{formatRangeDay(extendTarget?.end)}</span>. Pick the new return date — we check the vehicle's next reservation automatically.
          </div>
          <input
            type="date"
            value={extendDate}
            onChange={(e) => setExtendDate(e.target.value)}
            className="w-full rounded-xl border border-border px-3 py-2.5 text-sm"
            data-testid="extend-date-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExtendTarget(null)} className="rounded-full">Cancel</Button>
            <Button onClick={submitExtend} disabled={extendBusy || !extendDate} className="rounded-full" data-testid="extend-submit">
              {extendBusy ? "Checking…" : "Extend rental"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 84 — click-to-edit / remove an existing blocked range. */}
      <CalendarBlockEditDialog
        open={!!editBlock}
        car={editBlock ? (data.cars || []).find((c) => c.id === editBlock.carId) : null}
        range={editBlock?.range}
        onCancel={() => setEditBlock(null)}
        onSave={saveBlockEdit}
        onDelete={deleteBlockEdit}
        busy={editBusy}
      />
    </div>
  );
}
